import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Box, Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { selectLocationLoading } from "../../slices/location";
import FormikControl from "../../components/common/FormikControl";
import {
  addTemplate,
  disableClonedModal,
  disableModal,
  duplicateTemplate,
  editTemplate,
} from "../../slices/formTemplate";
import { notify } from "../../utils/methods/notification";
import { useNavigateTo } from "../../../lib/form-builder/hooks/useNavigate";
import { NAME_REGEX } from "../../../utils/common";

const initialValues = {
  template_type: 1,
  name: "",
  description: "",
  select_template: "",
  select_version: "",
};

const AddTemplateForm = ({ tailoredDuplicate, editTemplateData }) => {
  const dispatch = useDispatch();
  const navigateTo = useNavigateTo();
  const isLoading = useSelector(selectLocationLoading);
  const [versionsOptions, setVersionsOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { templateDropdownlist } = useSelector((state) => state.bmrFbl);
  const tempalteOptions = templateDropdownlist
    ?.map((items) => ({
      value: items?.id,
      label: items?.name,
    }))
    ?.sort((a, b) => a.label?.localeCompare(b.label));
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .min(3, "Name must be at least 3 character long")
          .max(50, "Name cannot exceed 50 characters")
          .matches(NAME_REGEX, "Enter a valid Name")
          .required("Name is required"),
        description: Yup.string()
          .trim()
          .min(3, "Description must be at least 3 character long")
          .max(255, "Description cannot exceed 255 characters")
          // .matches(NAME_REGEX, "Enter a valid Description")
          .required("Description is required"),
        select_version: Yup.string().when("template_type", {
          is: "2",
          then: Yup.string().required("Version is required"),
        }),
        select_template: Yup.string().when("template_type", {
          is: "2",
          then: Yup.string().required("Template is required"),
        }),
      }),
    [editTemplateData],
  );

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      if (editTemplateData) {
        try {
          await dispatch(
            editTemplate({
              id: editTemplateData.id,
              payload: { name: values.name },
            }),
          ).unwrap();
          dispatch(disableModal());
          dispatch(disableClonedModal());
        } catch (error) {
          notify.error(error || "Something went wrong");
        }
        setIsSubmitting(false);
        return;
      }
      if (!templateType) {
        const payload = {
          name: values.name,
          description: values.description,
        };
        try {
          const res = await dispatch(addTemplate(payload)).unwrap();
          notify.success(res.message);
          await dispatch(disableModal());
          dispatch(disableClonedModal());
          navigateTo(`/bmr/form-management/${res?.data?.id}`);
        } catch (error) {
          notify.error(error || "Something went wrong");
        }
      } else {
        const selectedVersionObj = tailoredDuplicate
          ? tailoredDuplicate.versions?.find(
              (v) => v.value === values.select_version,
            )
          : versionsOptions.find((v) => v.value === values.select_version);
        const payload = {
          name: values.name,
          version: values.select_version,
          description: values.description,
        };
        try {
          const response = await dispatch(
            duplicateTemplate({
              id: selectedVersionObj?.id || values.select_template,
              payload,
            }),
          ).unwrap();
          notify.success(response.message);
          dispatch(disableClonedModal());
          await dispatch(disableModal());
          navigateTo(`/bmr/form-management/${response?.data?.newTemplate?.id}`);
        } catch (error) {
          dispatch(disableModal());
          notify.error(
            error || error?.[0] || error?.message || "Something went wrong",
          );
        }
      }
    } catch (error) {
      dispatch(disableModal());
      dispatch(disableClonedModal());
      notify.error(error?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
      dispatch(disableModal());
      dispatch(disableClonedModal());
    }
  };

  const formik = useFormik({
    initialValues: editTemplateData
      ? { ...initialValues, name: editTemplateData.name ?? "" }
      : tailoredDuplicate
      ? {
          ...initialValues,
          template_type: 2,
          select_template: tailoredDuplicate?.select_template?.[0]?.value ?? "",
          select_version: tailoredDuplicate?.versions?.[0]?.value ?? "",
        }
      : initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  // remove leading spaces & handle null safely
  const handleChange = (e, field) => {
    let value = e.target.value;
    value = value.replace(/^\s+/, "");
    formik.setFieldValue(field, value);
  };

  let templateType = formik.values?.template_type == 2;

  const typeOptions = [
    { value: 1, label: "New template" },
    { value: 2, label: "Clone template" },
  ];

  const formOptions = editTemplateData
    ? [
        {
          label: "Name",
          type: "text",
          name: "name",
          required: true,
          placeholder: "Enter name",
          onChange: (e) => handleChange(e, "name"),
        },
      ]
    : [
        ...(!tailoredDuplicate
          ? [
              {
                label: "Select Template Type",
                type: "radio",
                name: "template_type",
                options: typeOptions,
                placeholder: "Select Template",
              },
            ]
          : []),
        {
          label: "Name",
          type: "text",
          name: "name",
          required: true,
          placeholder: "Enter name",
          onChange: (e) => handleChange(e, "name"),
        },
        ...(templateType
          ? [
              {
                label: "Select Template",
                type: "select",
                name: "select_template",
                placeholder: "Select template",
                options: tailoredDuplicate
                  ? tailoredDuplicate.select_template
                  : tempalteOptions,
                required: true,
                disabled: tailoredDuplicate ? true : false,
              },
              {
                label: "Select Version",
                type: "select",
                name: "select_version",
                options: tailoredDuplicate
                  ? tailoredDuplicate.versions
                  : versionsOptions,
                placeholder: "Select Version",
                required: true,
              },
            ]
          : []),
        {
          label: "Description",
          type: "textarea",
          name: "description",
          required: true,
          placeholder: "Enter description",
          onChange: (e) => handleChange(e, "description"),
        },
      ];

  const versionsOption = templateDropdownlist.find(
    (item) => item.id === formik.values.select_template,
  );

  useEffect(() => {
    if (versionsOption) {
      const versionList = versionsOption.versions.map((ver) => ({
        id: ver.id,
        value: ver.version,
        label: `V.${ver.version}`,
      }));
      setVersionsOptions(versionList);
    }
  }, [formik.values.select_template]);

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} className="mb-3">
          {formOptions.map((props, key) => (
            <Grid key={key} item xs={12}>
              <FormikControl formik={formik} {...props} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2} className="mt-3">
          <Grid item xs={12}>
            <LoadingButton
              loading={isLoading}
              disabled={isSubmitting}
              fullWidth
              type="submit"
              variant="contained"
            >
              {editTemplateData
                ? "Update"
                : templateType
                ? "Duplicate"
                : "Submit"}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AddTemplateForm;
